import { useCallback, useRef, useState } from 'react';
import { message } from 'antd';
import type {
  ResolvedStatisticsDateRange,
  StatisticsDateRange,
  StatisticsDateRangeStatus
} from '../types/date-range';

export function useStatisticsDateRange(
  initialPeriod = 'all',
  initialRange: StatisticsDateRange = [null, null]
) {
  const [period, setPeriod] = useState(initialPeriod);
  const [dateRange, setDateRange] = useState<StatisticsDateRange>(initialRange);
  const [resolvedFrom, setResolvedFrom] = useState<string | null>(null);
  const [status, setStatus] = useState<StatisticsDateRangeStatus>('idle');
  const [, refresh] = useState(0);
  const version = useRef(0);
  const [from, to] = dateRange;
  const invalidate = useCallback(() => {
    version.current += 1;
  }, []);
  const clearResolution = useCallback(() => {
    invalidate();
    setResolvedFrom(null);
    setStatus('idle');
  }, [invalidate]);

  const changePeriod = useCallback(
    (value: string) => {
      clearResolution();
      setPeriod(value);
      setDateRange([null, null]);
    },
    [clearResolution]
  );

  const applyDateRange = useCallback(
    (value: StatisticsDateRange) => {
      let [nextFrom, nextTo] = value;
      if (!from && resolvedFrom && nextFrom === resolvedFrom) nextFrom = null;
      if (nextFrom && !nextTo) {
        nextTo = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'Asia/Ho_Chi_Minh',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).format(new Date());
      }
      if (nextFrom && nextTo && nextFrom > nextTo) {
        message.error('Từ ngày phải nhỏ hơn hoặc bằng Đến ngày.');
        refresh((n) => n + 1);
        return;
      }
      if (
        nextFrom === from &&
        nextTo === to &&
        period === (nextFrom || nextTo ? 'custom' : 'all')
      )
        return;
      clearResolution();
      setDateRange([nextFrom, nextTo]);
      setPeriod(nextFrom || nextTo ? 'custom' : 'all');
      setStatus(!nextFrom && nextTo ? 'loading' : 'idle');
    },
    [from, to, period, resolvedFrom, clearResolution]
  );

  const beginResolution = useCallback(() => {
    const token = ++version.current;
    setResolvedFrom(null);
    setStatus(period === 'custom' && !from && to ? 'loading' : 'idle');
    return token;
  }, [period, from, to]);
  const finishResolution = useCallback(
    (token: number, result?: ResolvedStatisticsDateRange) => {
      if (token !== version.current || period !== 'custom' || from || !to)
        return;
      if (
        !result ||
        result.to_date !== to ||
        (result.from_date && result.from_date > to)
      ) {
        setStatus('error');
        return;
      }
      setResolvedFrom(result.from_date);
      setStatus(result.from_date ? 'resolved' : 'empty');
    },
    [period, from, to]
  );
  const failResolution = useCallback(
    (token: number) => {
      if (token === version.current && period === 'custom' && !from && to)
        setStatus('error');
    },
    [period, from, to]
  );

  return {
    period,
    dateRange,
    displayDateRange: [from || resolvedFrom, to] as StatisticsDateRange,
    status,
    changePeriod,
    applyDateRange,
    beginResolution,
    finishResolution,
    failResolution,
    invalidate
  };
}
